import {
  Button,
  Center,
  Flex,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useToast, ArrowLeftOutline } from '@aslanpay/portal-common-lib';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PaginationState } from '@tanstack/react-table';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import { defaultPageSize } from 'app/constants/table';
import { getHomePath } from 'app/utils/routes';

import { Table } from 'app/components/UI/Table';
import useFetchMerchants from 'app/hooks/useFetchMerchants';
import useFetchVoucherBrands from 'app/hooks/useFetchVoucherBrands';
import useUpdateBrandPriority from 'app/hooks/useUpdateBrandPriority';
import useUpdateVoucherRates from 'app/hooks/useUpdateVoucherRates';
import getMerchantsColumns from 'app/utils/table/merchants';
import { getVoucherBrandsColumns } from 'app/utils/table/vouchers';

import { EditableBrand } from './types';

const PAGE_SIZE_OPTIONS = [
  { value: 15, text: '15 rows / page' },
  { value: 25, text: '25 rows / page' },
  { value: 50, text: '50 rows / page' },
];

function Merchants() {
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editableBrands, setEditableBrands] = useState<EditableBrand[]>([]);

  const [merchantsPagination, setMerchantsPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const [brandsPagination, setBrandsPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const updateField = useCallback(
    <K extends keyof EditableBrand>(id: string, key: K, value: EditableBrand[K]) => {
      setEditableBrands((prev) => {
        // For priority changes, we need to match by brand ID, not promo ID
        if (key === 'priority') {
          return prev.map((brand) => (brand.id === id ? { ...brand, [key]: value } : brand));
        }
        // For other changes, use promo ID
        return prev.map((brand) => (brand.promoId === id ? { ...brand, [key]: value } : brand));
      });
    },
    []
  );

  const { data, isLoading, isError } = useFetchMerchants({
    page: merchantsPagination.pageIndex,
    size: merchantsPagination.pageSize,
    sortBy: 'id',
    sort: 'ASC',
  });

  const {
    data: brandsData,
    isLoading: isBrandsLoading,
    isError: isBrandsError,
    refetch: refetchBrands,
  } = useFetchVoucherBrands({
    page: brandsPagination.pageIndex,
    size: brandsPagination.pageSize,
    sortBy: 'id',
    sort: 'ASC',
  });

  const mutation = useUpdateVoucherRates({
    onSuccess: () => {},
    onError: () => {},
  });

  const priorityMutation = useUpdateBrandPriority({
    onSuccess: () => {},
    onError: () => {},
  });

  const mappedBrands: EditableBrand[] =
    brandsData?.list
      .filter((brand) => !!brand.promotion?.id)
      .map((brand) => ({
        ...brand,
        promoId: brand.promotion.id,
        promoName: brand.promotion.promoName || '',
        promoStartDate: brand.promotion.promoStartDate || '',
        promoEndDate: brand.promotion.promoEndDate || '',
        availableToUser: brand.promotion.commission?.availableToUser || 0,
        availableToUserForPaymentGateway:
          brand.promotion.commission?.availableToUserPaymentGateway || 0,
        // Read-only commission fields for display
        available: brand.promotion.commission?.available || 0,
        tillo: brand.promotion.commission?.tillo || 0,
        providerCommission: brand.promotion.commission?.provider || 0,
        paymentGatewayCommission: brand.promotion.commission?.paymentGateway || 0,
        availableToAslan: brand.promotion.commission?.availableToAslan || 0,
        availableToAslanPaymentGateway:
          brand.promotion.commission?.availableToAslanPaymentGateway || 0,
      })) ?? [];

  const handleSave = async () => {
    const invalidBrands = editableBrands.filter((brand) => !brand.promoId);

    if (invalidBrands.length > 0) {
      toast({
        title: 'Cannot save changes',
        description: 'One or more voucher brands are missing promo IDs.',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }

    const changedBrands = editableBrands.filter((editable) => {
      const original = mappedBrands.find((m) => m.promoId === editable.promoId);
      if (!original) return false;

      return (
        editable.availableToUser !== original.availableToUser ||
        editable.availableToUserForPaymentGateway !== original.availableToUserForPaymentGateway ||
        editable.priority !== original.priority
      );
    });

    if (changedBrands.length === 0) {
      toast({
        title: 'No changes to save',
        description: 'You have not modified any voucher rates or priorities.',
        status: 'info',
        position: 'top-right',
      });
      setIsEditing(false);
      return;
    }

    // Separate rate changes and priority changes
    const rateChanges = changedBrands.filter((editable) => {
      const original = mappedBrands.find((m) => m.promoId === editable.promoId);
      if (!original) return false;

      return (
        editable.availableToUser !== original.availableToUser ||
        editable.availableToUserForPaymentGateway !== original.availableToUserForPaymentGateway
      );
    });

    const priorityChanges = changedBrands.filter((editable) => {
      const original = mappedBrands.find((m) => m.promoId === editable.promoId);
      if (!original) return false;

      return editable.priority !== original.priority;
    });

    // Handle rate changes
    await Promise.all(
      rateChanges.map(async (brand) => {
        try {
          await mutation.mutateAsync({
            id: brand.promoId,
            availableToUser: brand.availableToUser,
            availableToUserForPaymentGateway: brand.availableToUserForPaymentGateway,
          });

          toast({
            title: `${brand.promoName || brand.name} rates updated`,
            description: `Voucher rates for ${brand.promoName || brand.name} have been updated.`,
            status: 'success',
            position: 'top-right',
          });
        } catch (error) {
          toast({
            title: `${brand.promoName || brand.name} rate update failed`,
            description: `Failed to update voucher rates for ${brand.promoName || brand.name}.`,
            status: 'error',
            position: 'top-right',
          });
        }
      })
    );

    // Handle priority changes
    if (priorityChanges.length > 0) {
      try {
        // Send ALL brands with their current priorities to ensure no data is lost
        await priorityMutation.mutateAsync(
          editableBrands.map((brand) => ({
            brandId: brand.id,
            priority: brand.priority,
          }))
        );

        toast({
          title: 'Priorities updated',
          description: `Priority for ${priorityChanges.length} brand(s) has been updated.`,
          status: 'success',
          position: 'top-right',
        });
      } catch (error) {
        toast({
          title: 'Priority update failed',
          description: `Failed to update priorities for ${priorityChanges.length} brand(s).`,
          status: 'error',
          position: 'top-right',
        });
      }
    }

    const totalChanges = rateChanges.length + priorityChanges.length;
    if (totalChanges > 0) {
      toast({
        title: 'All your updates are done',
        description: `All changes have been processed (${rateChanges.length} rate changes, ${priorityChanges.length} priority changes).`,
        status: 'success',
        position: 'top-right',
      });
    }

    await refetchBrands();
    setEditableBrands([]);
    setIsEditing(false);
    setBrandsPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  return (
    <AuthenticatedPageWrapper title="Merchants">
      <Flex justify="space-between" align="center" mb={4}>
        <Button
          size="sm"
          variant="link"
          leftIcon={<ArrowLeftOutline boxSize="20px" />}
          onClick={() => navigate(getHomePath())}
        >
          Back
        </Button>

        {activeTabIndex === 0 && !isEditing && (
          <Button
            onClick={() => {
              setIsEditing(true);
              setEditableBrands(mappedBrands);
              setBrandsPagination({ pageIndex: 0, pageSize: defaultPageSize });
            }}
            size="sm"
            colorScheme="purple"
          >
            Edit
          </Button>
        )}

        {activeTabIndex === 0 && isEditing && (
          <Flex gap={3}>
            <Button onClick={handleSave} size="sm" colorScheme="purple">
              Save changes
            </Button>
            <Button
              onClick={() => {
                setIsEditing(false);
                setEditableBrands([]);
                setBrandsPagination({ pageIndex: 0, pageSize: defaultPageSize });
              }}
              size="sm"
              variant="link"
              color="brand.purple"
            >
              Cancel
            </Button>
          </Flex>
        )}
      </Flex>

      <Tabs variant="unstyled" mt={2} onChange={(index) => setActiveTabIndex(index)}>
        <TabList borderBottom="1px solid #E2E8F0">
          <Tab
            _selected={{
              color: 'brand.purple',
              borderBottom: '2px solid',
              borderColor: 'brand.purple',
            }}
            px={4}
            py={2}
            fontWeight="medium"
            mr={4}
          >
            Voucher Brands
          </Tab>
          <Tab
            _selected={{
              color: 'brand.purple',
              borderBottom: '2px solid',
              borderColor: 'brand.purple',
            }}
            px={4}
            py={2}
            fontWeight="medium"
          >
            Merchants
          </Tab>
        </TabList>

        <TabPanels mt={4}>
          <TabPanel>
            {(() => {
              if (isBrandsLoading)
                return (
                  <Center h="200px">
                    <Spinner size="xl" color="brand.purple" />
                  </Center>
                );
              if (isBrandsError) {
                return (
                  <Center h="200px">
                    <Text color="red.500">Failed to load voucher brands.</Text>
                  </Center>
                );
              }

              return (
                <Table
                  columns={getVoucherBrandsColumns({
                    isEditing,
                    onChangeField: isEditing ? updateField : undefined,
                  })}
                  data={brandsData?.list}
                  total={brandsData?.total}
                  pageNumber={brandsPagination.pageIndex}
                  pageSize={brandsPagination.pageSize}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onPaginationChange={setBrandsPagination}
                />
              );
            })()}
          </TabPanel>

          <TabPanel>
            {(() => {
              if (isLoading) {
                return (
                  <Center h="200px">
                    <Spinner size="xl" color="brand.purple" />
                  </Center>
                );
              }
              if (isError) {
                return (
                  <Center h="200px">
                    <Text color="red.500">Failed to load merchants.</Text>
                  </Center>
                );
              }
              return (
                <Table
                  columns={getMerchantsColumns()}
                  data={
                    data?.list.map((m) => ({
                      ...m,
                      id: String(m.id),
                      name: m.name ?? '',
                      description: m.description ?? '',
                      url: m.url ?? '',
                      logoUrl: m.logoUrl ?? '',
                      categories: m.categories ?? [],
                      aliases: m.aliases ?? [],
                      isPopular: m.isPopular ?? false,
                    })) ?? []
                  }
                  total={data?.total ?? 0}
                  pageNumber={merchantsPagination.pageIndex}
                  pageSize={merchantsPagination.pageSize}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onPaginationChange={setMerchantsPagination}
                />
              );
            })()}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </AuthenticatedPageWrapper>
  );
}

export default Merchants;
